import { Box, Button, Checkbox, FormControlLabel, Link, Tooltip, Typography } from '@mui/material';
import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { Link as RouterLink } from 'react-router-dom';

/**
 * AcceptTerms component - displays a consent dialog for accepting legal agreements
 * @param {Object} props
 * @param {string} props.termsType - 'new-terms' or 'new-user' to determine the message displayed
 * @param {Function} props.onTermsAccepted - Callback function called after terms are accepted
 */
export default function AcceptTerms({ termsType, onTermsAccepted }) {
    const [, setCookie] = useCookies(['accepted_current_terms_version']);
    const [checkbox1Checked, setCheckbox1Checked] = useState(false);
    const [checkbox2Checked, setCheckbox2Checked] = useState(false);

    const message = termsType === 'new-terms'
        ? 'To continue using AttorneyAide, please review and accept our updated legal agreements.'
        : 'To continue using AttorneyAide, please review and accept our legal agreements.';

    const handleAcceptTerms = () => {
        setCookie('accepted_current_terms_version', 'true', { path: '/' });
        if (onTermsAccepted) {
            onTermsAccepted();
        }
    };

    return (
        <div style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: '100vh',
            background: 'var(--background-color)',
            color: 'var(--text-primary)'
        }}
        >
            <Box sx={{
                maxWidth: '600px',
                width: '90%',
                padding: 4,
                backgroundColor: 'var(--background-color)',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
            }}>
                <Typography
                    variant="h5"
                    sx={{
                        color: 'var(--text-primary)',
                        fontWeight: 600,
                        mb: 2,
                        textAlign: 'center'
                    }}
                >
                    Legal & Privacy Agreements
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: 'var(--text-primary)',
                        fontWeight: 400,
                        mb: 3,
                        textAlign: 'center'
                    }}
                >
                    {message}
                </Typography>

                <Box
                    sx={{
                        mb: 3,
                        p: 2.5,
                        borderRadius: '8px',
                        border: `1px solid var(--border-color)`,
                        backgroundColor: 'var(--surface-color)',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <Box sx={{ mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checkbox1Checked}
                                    onChange={(e) => setCheckbox1Checked(e.target.checked)}
                                    sx={{
                                        color: 'var(--text-primary)',
                                        '&.Mui-checked': {
                                            color: 'var(--accent-color)'
                                        },
                                        paddingTop: '0px'
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: '0.95rem',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    I agree to the{' '}
                                    <Link
                                        component={RouterLink}
                                        to="/terms"
                                        sx={{
                                            color: 'var(--text-primary)',
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': {
                                                color: 'var(--text-secondary)',
                                                opacity: 0.8
                                            }
                                        }}
                                    >
                                        Terms of Service
                                    </Link>
                                    {' '}and{' '}
                                    <Link
                                        component={RouterLink}
                                        to="/privacy"
                                        sx={{
                                            color: 'var(--text-primary)',
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': {
                                                color: 'var(--text-secondary)',
                                                opacity: 0.8
                                            }
                                        }}
                                    >
                                        Privacy Policy
                                    </Link>
                                    .
                                </Typography>
                            }
                            sx={{
                                alignItems: 'flex-start',
                                '& .MuiFormControlLabel-label': {
                                    marginTop: 0
                                }
                            }}
                        />
                    </Box>

                    <Box>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={checkbox2Checked}
                                    onChange={(e) => setCheckbox2Checked(e.target.checked)}
                                    sx={{
                                        color: 'var(--text-primary)',
                                        '&.Mui-checked': {
                                            color: 'var(--accent-color)'
                                        },
                                        paddingTop: '0px'
                                    }}
                                />
                            }
                            label={
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: '0.95rem',
                                        lineHeight: 1.6,
                                    }}
                                >
                                    I represent that I have the legal authority to upload these documents and that the relevant data subjects have been notified or consented to this processing. I agree to the{' '}
                                    <Link
                                        component={RouterLink}
                                        to="/chdpp"
                                        sx={{
                                            color: 'var(--text-primary)',
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': {
                                                color: 'var(--text-secondary)',
                                                opacity: 0.8
                                            }
                                        }}
                                    >
                                        Consumer Health Data Privacy Policy
                                    </Link>
                                    {' '}and the{' '}
                                    <Link
                                        component={RouterLink}
                                        to="/baa"
                                        sx={{
                                            color: 'var(--text-primary)',
                                            textDecoration: 'underline',
                                            fontWeight: 500,
                                            '&:hover': {
                                                color: 'var(--text-secondary)',
                                                opacity: 0.8
                                            }
                                        }}
                                    >
                                        Business Associate Agreement
                                    </Link>
                                    .
                                </Typography>
                            }
                            sx={{
                                alignItems: 'flex-start',
                                '& .MuiFormControlLabel-label': {
                                    marginTop: 0
                                }
                            }}
                        />
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip
                        title={(!checkbox1Checked || !checkbox2Checked) ? "Please check both boxes to continue" : ""}
                        arrow
                        placement="top"
                    >
                        <span>
                            <Button
                                onClick={handleAcceptTerms}
                                disabled={!checkbox1Checked || !checkbox2Checked}
                                sx={{
                                    background: 'var(--button-gradient)',
                                    color: 'white',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    borderRadius: '12px',
                                    px: 4,
                                    py: 1.5,
                                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    '&:hover': {
                                        background: 'var(--button-gradient-hover)',
                                        boxShadow: '0 8px 25px rgba(212, 175, 55, 0.5)',
                                        transform: 'translateY(-2px)'
                                    },
                                    '&:disabled': {
                                        background: 'var(--text-secondary)',
                                        opacity: 0.5,
                                        color: 'white'
                                    },
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: '-100%',
                                        width: '100%',
                                        height: '100%',
                                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                        transition: 'left 0.6s'
                                    },
                                    '&:hover::before': {
                                        left: '100%'
                                    }
                                }}
                            >
                                Accept and Continue
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        </div>
    );
}
